import type {AccessGrantSourceConnection} from './access_grants.js';
import type {Audience} from '#lit/types/audiences.js';
import type {ExpressionTypes} from '#lit/types/builders/audience_builder/index.js';
import type {FormSelectFieldOption} from '#lit/components/base/form_select_field/index.js';
import {v4} from 'uuid';
import {isString} from '#lit/utils/index.js';
import type {Nullable} from './general.js';
import type {Field} from '#lit/lib/immutable_form.js';
import type {SerializedStore} from '#lit/lib/builder/stores/graphology.js';

export enum DatasetStatus {
  ACTIVE = 'active',
  AUTHORIZED = 'authorized',
}

export enum DatasetType {
  /** Original ones */

  /* This represents users or individual customers. */
  USER = 'customer_details',

  TRANSACTION = 'transactions',

  /* Additional information about your web or mobile events. */
  EVENT = 'events',

  /** Added later */

  /* This typically represents companies in your database. */
  ACCOUNT = 'account',

  /* Identifies a household that can have multiple individuals. */
  HOUSEHOLD = 'household',

  /* Actions in your mobile like logins, searches, add to cart. */
  MOBILE_EVENTS = 'mobile_events',

  /* Additional information about your web or mobile events. */
  EVENT_INFORMATION = 'event_information',

  /* Email sends, opens, clicks */
  EMAIL_INTERACTIONS = 'email_interactions',

  /* Posts by users on your platform */
  POSTS = 'posts',

  /* Additional information about your products. */
  PRODUCT_INFORMATION = 'product_information',

  /*
    Individual products purchases as part of a larger order.
    Usually used in retail for “Carts”
    and associated with Purchase Dataset type.
  */
  TRANSACTION_LINE_ITEMS = 'transaction_line_items',

  /* Push notification views, taps, accepts. */
  PUSH_NOTIFICATION_INTERACTIONS = 'push_notification_interactions',

  /* Returns when customers return an item */
  RETURNS = 'returns',

  /* Reviews by users on your platform */
  REVIEWS = 'reviews',

  /* Searches by user on your platform */
  SEARCHES = 'searches',

  /* Survey responses */
  SURVEY_INTERACTIONS = 'survey_interactions',

  /* Additional information about your users. */
  USER_INFORMATION = 'user_information',

  /* Actions on your website like logins, searches, add to cart. */
  WEBSITE_EVENTS = 'website_events',

  OTHER_EVENTS = 'other_events',
  OTHER_TRANSACTIONS = 'other_transactions',
  OTHER_METADATA = 'other_metadata',
  OTHER_DATASET = 'other_dataset',
}

export type OriginalDatasetTypes =
  | DatasetType.USER
  | DatasetType.TRANSACTION
  | DatasetType.EVENT;

export const DATASET_TYPE_LABEL_TYPE = {
  [DatasetType.USER]: 'Users',
  [DatasetType.TRANSACTION]: 'Transactions',
  [DatasetType.EVENT]: 'Events',
  [DatasetType.ACCOUNT]: 'Account',
  [DatasetType.HOUSEHOLD]: 'Household',
  [DatasetType.USER_INFORMATION]: 'User Information',
  [DatasetType.WEBSITE_EVENTS]: 'Website Events',
  [DatasetType.MOBILE_EVENTS]: 'Mobile Events',
  [DatasetType.TRANSACTION_LINE_ITEMS]: 'Transaction Line Items',
  [DatasetType.RETURNS]: 'Returns',
  [DatasetType.PRODUCT_INFORMATION]: 'Product Information',
  [DatasetType.EVENT_INFORMATION]: 'Event Information',
  [DatasetType.EMAIL_INTERACTIONS]: 'Email Interactions',
  [DatasetType.PUSH_NOTIFICATION_INTERACTIONS]:
    'Push Notification Interactions',
  [DatasetType.SURVEY_INTERACTIONS]: 'Survey Interactions',
  [DatasetType.POSTS]: 'Posts',
  [DatasetType.REVIEWS]: 'Reviews',
  [DatasetType.SEARCHES]: 'Searches',
  [DatasetType.OTHER_EVENTS]: 'Other Events',
  [DatasetType.OTHER_TRANSACTIONS]: 'Other Transactions',
  [DatasetType.OTHER_METADATA]: 'Other Metadata',
  [DatasetType.OTHER_DATASET]: 'Other Dataset',
} as const;

export const DATASET_TYPE_LABEL_VERBS = {
  [DatasetType.USER]: 'Has',
  [DatasetType.TRANSACTION]: 'Performed',
  [DatasetType.EVENT]: 'Performed',
  [DatasetType.ACCOUNT]: 'Has',
  [DatasetType.HOUSEHOLD]: 'Has',
  [DatasetType.USER_INFORMATION]: 'Has',
  [DatasetType.WEBSITE_EVENTS]: 'Performed',
  [DatasetType.MOBILE_EVENTS]: 'Performed',
  [DatasetType.TRANSACTION_LINE_ITEMS]: 'Has',
  [DatasetType.RETURNS]: 'Has',
  [DatasetType.PRODUCT_INFORMATION]: 'Has',
  [DatasetType.EVENT_INFORMATION]: 'Has',
  [DatasetType.EMAIL_INTERACTIONS]: 'Has',
  [DatasetType.PUSH_NOTIFICATION_INTERACTIONS]: 'Performed',
  [DatasetType.SURVEY_INTERACTIONS]: 'Performed',
  [DatasetType.POSTS]: 'Has',
  [DatasetType.REVIEWS]: 'Has',
  [DatasetType.SEARCHES]: 'Has',
  [DatasetType.OTHER_EVENTS]: 'Performed',
  [DatasetType.OTHER_TRANSACTIONS]: 'Has',
  [DatasetType.OTHER_METADATA]: 'Has',
  [DatasetType.OTHER_DATASET]: 'Matches',
} as const;

type DatasetTypeLabelType = typeof DATASET_TYPE_LABEL_TYPE;

type DatasetTypeColorType = {
  [DatasetType.USER]: 'primary--light';
  [DatasetType.TRANSACTION]: 'green--light';
  [DatasetType.EVENT]: 'secondary--light';
};

type DatasetTypeIconType = {
  [DatasetType.USER]: '/assets/dataset_type_user.svg';
  [DatasetType.TRANSACTION]: '/assets/dataset_type_transaction.svg';
  [DatasetType.EVENT]: '/assets/dataset_type_event.svg';
};

export type DatasetTypeObject<T extends DatasetType> = {
  type: T;
  label: DatasetTypeLabelType[T];
  color: T extends OriginalDatasetTypes ? DatasetTypeColorType[T] : string;
  icon: T extends OriginalDatasetTypes ? DatasetTypeIconType[T] : null;
};

export type FlexDatasets = DatasetType.TRANSACTION | DatasetType.EVENT;

export enum EventDatasetCategory {
  WEB = 'web',
  MOBILE_APP = 'mobile_app',
  EMAIL = 'email',
  OTHER = 'other',
}

export enum FieldIsUnique {
  YES = 'yes', // The field is unique in the dataset
  NO = 'no', // There are duplicate values in the dataset for this field
  FAIL = 'fail', // There was an error checking the uniqueness of the field
  CHECKING = 'checking', // The field is being checked for uniqueness
}

export type FieldIsUniqueWithMessage = {
  isUnique: FieldIsUnique;
  message?: string;
};

interface DatasetFieldForKey {
  id: string;
  field: string;
}

export interface FlexJoinKey extends DatasetFieldForKey {
  type?: ExpressionTypes;
}

export type CustomerJoinKey = DatasetFieldForKey;
export type PrimaryKey = DatasetFieldForKey;

export interface DatasetTableField extends FormSelectFieldOption {
  example_value?: string;
  type?: ExpressionTypes;
  id?: number;
  name_alias?: string;
}

export interface PrimaryDatasetField {
  name: string;
  description: string;
  value: string;
}

export interface JoinConfigurationFieldSet {
  id?: number;
  datasetGroupId?: number;
  customerDetailsId?: number;
  joinCardinality: JoinCardinalities;
  flexJoinKeys: FlexJoinKey[];
  customerDetailsJoinKeys: CustomerJoinKey[];
  fields?: DatasetTableField[];
  loading?: boolean;
}

export interface DatasetGroup {
  id: number;
  name: string;
  description?: string | null;
  source_connection_id: number;
  source_connection_name: string;
  source_connection_icon: string;
  primary_dataset?: Dataset;
  primary_dataset_id: number;
  primary_dataset_name: string;
  team_id: number;
  datasets?: Dataset[];
  audiences?: Audience[];
  audiences_count?: number;
  dataset_count?: number;
  default_personalizations: string[];
  marve_approval: boolean;
  settings?: {
    minimum_audience_export_size?: number;
    minimum_audience_report_size?: number;
  };
}

/**
 * TODO: Review with BE what are the possible fields
 * for the dataset field mapping.
 * The current implementation is not entirely type-safe.
 * Setting it to a Record<string, string | number | undefined> allows
 * runtime errors on consuming inexistent fields,
 * but it is still a bit better than {[k:string]: string}
 */
export interface DatasetFieldMapping
  extends Record<string, string | number | undefined> {
  email_field: string;
  mobile_device_id: string;
  client_customer_id: string;
  total_spend_denomination: 'dollars' | 'cents';
  total_spend_in_last_year: string;
  default_treatment?: number;
  user_consent: string;
  user_data_consent: string;
  ad_personalization_consent: string;
}

export interface DatasetDefaultField {
  id: string;
  name: string;
  dataset: string;
  table: string;
  type: string;
  display_name: string;
  description?: string;
}

export interface DatasetLabel {
  id: number;
  section: string;
  display_name: string;
  field_name: string;
  field_type: string;
  created_at: string;
  updated_at: string;
  description: string;
}

export interface DatasetFieldLabel {
  id?: number | null;
  label_id: number;
  dataset_id?: number | null;
  dataset_field: string;
}

export interface Dataset {
  created_at: string;
  updated_at: string;
  id: number;
  name: string;
  description: string;
  database: string;
  dataset: string;
  flywheel_model_id?: number;
  duplicate_resolution_method?: string;
  null_resolution_method?: string;
  platform_dataset: string;
  project_dataset: string;
  dataset_without_db: string;
  email_field_name: string;
  default_fields: string[];
  pii_fields: string[];
  exclude_custom_fields: string[];
  default_field_objects: Record<string, DatasetDefaultField>[];
  unique_fields: string[];
  project_id: string;
  dataset_type: DatasetType;
  source_connection_id: number;
  source_connection_name: string;
  source_connection_icon: string;
  source_table: string;
  status: DatasetStatus;
  team_id: number;
  set_type?: string;
  columns?: number;
  rows?: number;
  field_mapping: DatasetFieldMapping;
  dataset_groups?: DatasetGroup[];
  right_join_configurations?: JoinConfiguration[];
  left_join_configurations?: JoinConfiguration[];
  category?: EventDatasetCategory;
  labels?: DatasetLabel[];
  dataset_field_labels?: DatasetFieldLabel[];
  audience_active_export_count: number;
  audience_export_count: number;
  audience_count: number;
  signal_count: number;
  table_last_updated_at: string;
  primary_ready: boolean;
  filters?: SerializedStore;
  active_dependencies?: string;
}

export interface SourceConnection {
  id: number;
  team_id: number;
  description: string;
  source_type: string;
  source_name: string;
  source_credentials: SourceConnectionCredentials;
  last_warehouse_refresh: WarehouseRefreshInfo;
  access_grants: AccessGrantSourceConnection[];
  dataset_groups: DatasetGroup[];
  icon: string;
  created_at: string;
  updated_at: string;
  system_catalog_and_schema: string;
  realtime: boolean;
  disable_value_caching: boolean;
}

export interface QueueEvent {
  id: number;
  display_name: string;
  enabled: boolean;
  schema: string;
}

export interface WarehouseRefreshInfo {
  state: string;
  synched_at?: string;
}

export interface SourceConnectionCredentials {
  project_id?: string;
  client_id?: string;
  client_email?: string;
  account?: string;
  user?: string;
  role?: string;
  hostname?: string;
  http_path?: string;
  catalog?: string;
}

export interface JoinConfiguration {
  id?: number;
  dataset_group_id: number;
  left_dataset_id: number;
  right_dataset_id?: number;
  config: {
    join_cardinality: JoinCardinalities;
    join_table?: {
      schema: string;
      table_name: string;
      left_table_field: string;
      right_table_field: string;
    };
    right_dataset_join_keys: string[];
    left_dataset_join_keys: string[];
  };
}

// -- TYPE GUARDS

export function isDatasetType(x: unknown): x is DatasetType {
  return isString(x) && Object.values<string>(DatasetType).includes(x);
}

// -- HELPERS

export const emptyDatasetKeyWithId = (id: string) => ({id: id, field: ''});
export const emptyDatasetKey = () => emptyDatasetKeyWithId(v4());

export const emptyFlexJoinKeyWithId = (id: string) => ({
  id: id,
  field: '',
  type: undefined,
});

export const emptyFlexJoinKey = () => emptyFlexJoinKeyWithId(v4());

export const fieldDatasetKeyWithId = (id: string, field: string) => ({
  id: id,
  field: field,
});

export const fieldDatasetKey = (field: string) =>
  fieldDatasetKeyWithId(v4(), field);

export const fieldFlexJoinKeyWithId = (id: string, field: string) => ({
  id: id,
  field: field,
  type: undefined,
});

export const fieldFlexJoinKey = (field: string) =>
  fieldFlexJoinKeyWithId(v4(), field);

export enum JoinCardinalities {
  ONE_TO_ONE = '1-1',
  ONE_TO_MANY = '1-N',
  MANY_TO_ONE = 'N-1',
  MANY_TO_MANY = 'N-N',
}

export function invertJoinCardinality(cardinality: JoinCardinalities) {
  switch (cardinality) {
    case JoinCardinalities.ONE_TO_ONE:
      return JoinCardinalities.ONE_TO_ONE;
    case JoinCardinalities.ONE_TO_MANY:
      return JoinCardinalities.MANY_TO_ONE;
    case JoinCardinalities.MANY_TO_ONE:
      return JoinCardinalities.ONE_TO_MANY;
    case JoinCardinalities.MANY_TO_MANY:
      return JoinCardinalities.MANY_TO_MANY;
  }
}

export const emptyFieldSet = () => ({
  joinCardinality: JoinCardinalities.ONE_TO_MANY,
  flexJoinKeys: [],
  customerDetailsJoinKeys: [],
});

export const defaultJoinConfigurationFieldSets = () => [emptyFieldSet()];

export const datasetTypeLabel: Readonly<{
  get<T extends DatasetType>(type: T): DatasetTypeLabelType[T];
}> = {
  get<T extends DatasetType>(type: T): DatasetTypeLabelType[T] {
    return DATASET_TYPE_LABEL_TYPE[type];
  },
};

export const datasetTypeColor: Readonly<{get(type: DatasetType): string}> = {
  get(type: DatasetType): string {
    switch (type) {
      case DatasetType.USER:
        return 'primary--light';
      case DatasetType.TRANSACTION:
        return 'green--light';
      case DatasetType.EVENT:
        return 'secondary--light';
      default:
        return 'secondary--light';
    }
  },
};

export const datasetTypeIcon: Readonly<{
  get(type: DatasetType): Nullable<string>;
}> = {
  get(type: DatasetType): Nullable<string> {
    switch (type) {
      case DatasetType.USER:
        return '/assets/dataset_type_user.svg';
      case DatasetType.TRANSACTION:
        return '/assets/dataset_type_transaction.svg';
      case DatasetType.EVENT:
        return '/assets/dataset_type_event.svg';
      default:
        return null;
    }
  },
};

export const getDatasetTypeObject = <T extends DatasetType>(
  datasetType?: T
): DatasetTypeObject<T> | undefined => {
  if (!datasetType) return undefined;

  const type = datasetType;
  const icon = datasetTypeIcon.get(datasetType);
  const color = datasetTypeColor.get(datasetType);
  const label = datasetTypeLabel.get(datasetType);

  return {
    type,
    icon,
    color,
    label,
  } as DatasetTypeObject<T>;
};

const ALL_DATASET_TYPE_OBJECTS = Object.values(DatasetType).map(
  getDatasetTypeObject
) as DatasetTypeObject<DatasetType>[];

export const getAllDatasetTypes = () => {
  return ALL_DATASET_TYPE_OBJECTS;
};

export type DatasetLabelMap = Map<string, DatasetLabel>;

export function addLabel(labels: DatasetLabelMap, label: DatasetLabel) {
  labels.set(buildLabelKey(label.section, label.field_name), label);
}

export function getLabel(
  labels: DatasetLabelMap,
  section: string,
  field_name: string
): DatasetLabel {
  return labels.get(buildLabelKey(section, field_name))!;
}

export function hasLabel(
  labels: DatasetLabelMap,
  section: string,
  field_name: string
): boolean {
  return labels.has(buildLabelKey(section, field_name));
}

// We can have the same field_name for different sections, so we need to
// differentiate them by section.
function buildLabelKey(section: string, field_name: string): string {
  if (section === DatasetType.USER) section = 'users';

  return `${section}-${field_name}`;
}

export enum DatasetGroupTableModal {
  EDIT = 'edit',
  DELETE = 'delete',
  ADD_DATASET = 'add_dataset',
}

export type NewDatasetGroupFields = {
  id: Field<Nullable<number>, 'fly-input'>;
  team: Field<Nullable<number>, 'fly-select'>;
  sourceConnection: Field<Nullable<number>, 'fly-select'>;
  name: Field<string, 'fly-input'>;
  description: Field<string, 'fly-textarea'>;
  primaryDatasetId: Field<Nullable<number>, 'fly-select'>;
  personalizations: Field<string[], 'default-personalizations-multiselect'>;
  exportSize: Field<Nullable<number>, 'fly-input'>;
  reportSize: Field<Nullable<number>, 'fly-input'>;
};

export type PrimaryTableField = {
  dataset: string;
  description: string;
  display_name: string;
  fw_type: string;
  id: string;
  name: string;
  name_alias: Nullable<string>;
  table: string;
  type: string;
};

export type DatasetGroupMetadata = {
  id: number;
  name: string;
};
